import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';
import TermsOfService from './docs/TermsOfService';
import PrivacyPolicy from './docs/PrivacyPolicy';
import RefundPolicy from './docs/RefundPolicy';
import HowItWorks from './HowItWorks';
import FAQ from './FAQ';

// Отдельный компонент формы запроса сброса пароля
function ResetPasswordForm({ onClose }) {
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');
    setResetLoading(true);

    try {
      const response = await api.post('request-password-reset/', { email: resetEmail });
      setResetMessage(response.data?.message || 'Инструкции по сбросу пароля отправлены на ваш Email!');
    } catch (err) {
      setResetError(err.response?.data?.email?.[0] || err.response?.data?.error || 'Пользователь с таким email не найден.');
    } finally {
      setResetLoading(false);
    }
  };

  return resetMessage ? (
    <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-medium text-center">
      {resetMessage}
    </div>
  ) : (
    <form onSubmit={handleResetPassword} className="space-y-3">
      {resetError && (
        <div className="p-2 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs">
          {resetError}
        </div>
      )}
      <div>
        <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Email</label>
        <input 
          type="email" 
          required 
          value={resetEmail} 
          onChange={(e) => setResetEmail(e.target.value)} 
          placeholder="example@gmail.com"
          className="w-full bg-[#1f2937]/50 border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl py-2 px-3 text-white text-sm outline-none transition duration-200"
        />
      </div>
      <button 
        type="submit" 
        disabled={resetLoading}
        className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition cursor-pointer shadow-lg shadow-indigo-600/20"
      >
        {resetLoading ? 'Отправка...' : 'Отправить ссылку'}
      </button>
    </form>
  );
}

function LoginForm({ setUser, onSwitchToRegister }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Состояние для документов, "Как это работает" и FAQ
  const [activeDoc, setActiveDoc] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  
  // Состояние для окна сброса пароля
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    
    html.style.setProperty('margin', '0', 'important');
    html.style.setProperty('padding', '0', 'important');
    html.style.setProperty('background-color', '#090d16', 'important');
    
    body.style.setProperty('margin', '0', 'important');
    body.style.setProperty('padding', '0', 'important');
    body.style.setProperty('background-color', '#090d16', 'important');

    return () => {
      html.style.backgroundColor = '';
      body.style.backgroundColor = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('token/', { username, password });
      localStorage.setItem('token', res.data.access);
      
      const userRes = await api.get('users/me/');
      localStorage.setItem('user', JSON.stringify(userRes.data));
      setUser(userRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Неверный логин или пароль');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#090d16',
        display: 'flex',
        overflow: 'hidden',
        zIndex: 9999
      }}
      className="text-white"
    >
      <Helmet>
        <title>Фриланс-биржа с прозрачной комиссией 7% — RealWork.pro</title>
        <meta name="description" content="Безопасная биржа фриланса с выплатами в USDT. Единая комиссия 7% без скрытых сборов, защита Escrow и быстрый поиск исполнителей." />
        <meta property="og:title" content="Фриланс-биржа RealWork.pro — Заказы и работа в USDT" />
        <meta property="og:description" content="Единая комиссия 7%, безопасные сделки Escrow и прямые выплаты в USDT." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ЛЕВАЯ ЧАСТЬ: МАРКЕТИНГОВЫЙ БЛОК */}
      <div 
        className="hidden md:flex md:w-1/2 relative flex-col justify-between p-10 lg:p-14 bg-cover bg-center h-full border-r border-slate-800/60 overflow-y-auto custom-scroll"
        style={{ 
          backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.96), rgba(17, 24, 39, 0.98)), url('http://googleusercontent.com/image_collection/image_retrieval/498531985610772507_0')` 
        }}
      >
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-500 rounded-full blur-[120px] opacity-20 pointer-events-none"></div>
        
        <div className="flex items-center space-x-3 z-10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <span className="font-bold text-xl tracking-wider text-white">R</span>
          </div>
          <span className="font-bold text-2xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
            Realwork<span className="text-indigo-400">.pro</span>
          </span>
        </div>

        <div className="my-auto max-w-lg z-10 py-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            Биржа фриланса нового поколения
          </div>

          <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mb-4 text-white">
            Честный фриланс <br />
            с фиксированной <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">комиссией 7%</span>
          </h1>

          <p className="text-gray-300 text-sm lg:text-base mb-6 leading-relaxed">
            Безопасные сделки, прямые расчеты в USDT и быстрый поиск исполнителей. Мы убрали двойные сборы, чтобы заказчики не переплачивали, а исполнители зарабатывали больше.
          </p>

          {/* СПИСОК ПРЕИМУЩЕСТВ */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start space-x-4 group">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 shrink-0 group-hover:bg-indigo-500/20 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zm7 7a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-sm lg:text-base text-white">Единая комиссия 7% — без скрытых поборов</h3>
                <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">Берем комиссию один раз с заказа, а не дважды (с исполнителя и заказчика).</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 group">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 shrink-0 group-hover:bg-purple-500/20 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-sm lg:text-base text-white">Безопасные сделки (Escrow)</h3>
                <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">Оплата замораживается на платформе до приемки работы заказчиком.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 group">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-sm lg:text-base text-white">Мгновенные выплаты в USDT</h3>
                <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">Фиксированный курс и быстрый вывод на ваш криптовалютный кошелек.</p>
              </div>
            </div>
          </div>

          {/* ИНТЕГРИРОВАННАЯ КНОПКА "КАК ЭТО РАБОТАЕТ" */}
          <button
            onClick={() => setShowHowItWorks(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold transition duration-200 cursor-pointer"
          >
            <span>💡 Впервые у нас? Узнайте, как работает Escrow</span>
            <span>→</span>
          </button>
        </div>

        <div className="text-gray-500 text-xs z-10 pt-4">
          © 2026 Realwork.pro. Платформа честного фриланса.
        </div>
      </div>

      {/* ПРАВАЯ ЧАСТЬ: ФОРМА ВХОДА */}
      <div className="w-full md:w-1/2 flex flex-col justify-between items-center p-6 bg-[#090d16] h-full overflow-y-auto">
        <div className="hidden md:block h-10"></div>

        <div className="w-full max-w-sm bg-[#111827]/60 p-6 rounded-2xl border border-gray-800 backdrop-blur-md shadow-2xl space-y-4 my-auto">
          <div className="text-center md:text-left flex items-center space-x-3 md:space-x-0 md:block">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-indigo-500/10 text-indigo-400 rounded-xl text-lg md:mb-2 border border-indigo-500/20 shrink-0">
              🔐
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Войти в аккаунт</h2>
              <p className="text-gray-400 text-xs mt-0.5">Добро пожаловать обратно</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs font-medium">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Логин</label>
              <input
                type="text"
                required
                placeholder="Ваш login"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full bg-[#1f2937]/50 border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl py-2 px-3 text-white placeholder-gray-500 outline-none transition duration-200 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Пароль</label>
              <input
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-[#1f2937]/50 border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl py-2 px-3 text-white placeholder-gray-500 outline-none transition duration-200 text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 disabled:cursor-not-allowed text-white font-bold transition duration-200 shadow-lg shadow-indigo-600/20 text-sm mt-1 cursor-pointer"
            >
              {loading ? 'Авторизация...' : 'Войти в панель'}
            </button>

            {/* БЛОК ССЫЛОК: СОЗДАТЬ АККАУНТ И ЗАБЫЛИ ПАРОЛЬ */}
            <div className="flex items-center justify-between pt-2 text-xs">
              <button
                type="button"
                onClick={onSwitchToRegister}
                className="text-indigo-400 hover:text-indigo-300 font-medium transition duration-200 hover:underline cursor-pointer"
              >
                Создать аккаунт
              </button>

              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-sky-400 hover:text-sky-300 font-medium transition duration-200 hover:underline cursor-pointer"
              >
                Забыли пароль?
              </button>
            </div>
          </form>
        </div>

        {/* МИНИ-ФУТЕР */}
        <div className="w-full max-w-sm text-center pt-6 pb-2 border-t border-slate-800/40 mt-4">
          <div className="flex justify-center gap-3 text-[11px] text-gray-500 flex-wrap">
            <button 
              onClick={() => setShowHowItWorks(true)} 
              className="text-indigo-400 hover:text-indigo-300 font-medium transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Как это работает
            </button>
            <span>•</span>
            <button 
              onClick={() => setShowFAQ(true)} 
              className="text-indigo-400 hover:text-indigo-300 font-medium transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              FAQ
            </button>
            <span>•</span>
            <button 
              onClick={() => setActiveDoc('terms')} 
              className="hover:text-indigo-400 transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Соглашение
            </button>
            <span>•</span>
            <button 
              onClick={() => setActiveDoc('privacy')} 
              className="hover:text-indigo-400 transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Конфиденциальность
            </button>
            <span>•</span>
            <button 
              onClick={() => setActiveDoc('refund')} 
              className="hover:text-indigo-400 transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Возврат
            </button>
          </div>
          <div className="text-[10px] text-gray-600 mt-1.5">
            Поддержка: <a href="mailto:realwork.support@gmail.com" className="text-indigo-400/80 hover:underline hover:text-indigo-300 transition">realwork.support@gmail.com</a>
          </div>
        </div>
      </div>

      {/* --- МОДАЛЬНЫЙ СЛОЙ: КАК ЭТО РАБОТАЕТ --- */}
      {showHowItWorks && (
        <div className="fixed inset-0 bg-[#090d16] z-[10000] overflow-y-auto">
          <HowItWorks onClose={() => setShowHowItWorks(false)} />
        </div>
      )}

      {/* --- МОДАЛЬНЫЙ СЛОЙ: FAQ --- */}
      {showFAQ && (
        <div className="fixed inset-0 bg-[#090d16] z-[10000] overflow-y-auto">
          <FAQ onClose={() => setShowFAQ(false)} />
        </div>
      )}

      {/* --- ВСПЛЫВАЮЩИЕ ОКНА ДЛЯ ДОКУМЕНТОВ --- */}
      {activeDoc && (
        <div 
          onClick={() => setActiveDoc(null)} 
          className="fixed inset-0 bg-black/80 flex justify-center items-center p-4"
          style={{ zIndex: 10001 }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto relative text-slate-800 shadow-2xl"
          >
            <button 
              onClick={() => setActiveDoc(null)} 
              className="absolute top-4 right-4 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold py-1 px-3 rounded-lg text-xs transition cursor-pointer"
            >
              ✕ Закрыть
            </button>
            <div className="mt-4">
              {activeDoc === 'terms' && <TermsOfService />}
              {activeDoc === 'privacy' && <PrivacyPolicy />}
              {activeDoc === 'refund' && <RefundPolicy />}
            </div>
          </div>
        </div>
      )}

      {/* --- МОДАЛЬНОЕ ОКНО СБРОСА ПАРОЛЯ --- */}
      {showResetModal && (
        <div 
          onClick={() => setShowResetModal(false)} 
          className="fixed inset-0 bg-black/80 flex justify-center items-center p-4"
          style={{ zIndex: 10001 }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-[#111827] border border-gray-800 rounded-2xl p-6 max-w-md w-full relative text-white shadow-2xl space-y-4"
          >
            <button 
              onClick={() => setShowResetModal(false)} 
              className="absolute top-4 right-4 text-gray-400 hover:text-white font-bold text-sm transition cursor-pointer"
            >
              ✕
            </button>

            <div>
              <h3 className="text-xl font-bold">Восстановление пароля</h3>
              <p className="text-gray-400 text-xs mt-1">Укажите Email, связанный с вашим аккаунтом. Мы отправим ссылку для сброса.</p>
            </div>

            <ResetPasswordForm onClose={() => setShowResetModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginForm;